import { track } from "@/lib/analytics";
import { Component, lazy, Suspense, useCallback, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { SectionIntro } from "@/components/site/SectionHead";
import { PillLink } from "@/components/site/Buttons";

const PrathamScene = lazy(() => import("@/components/three/PrathamScene"));
class ViewerBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onStatus("fallback"); }
  render() { return this.state.failed ? null : this.props.children; }
}

const MODES = [
  { id: "empty", label: "Empty deck", description: "Room for what needs to move." },
  { id: "pallets", label: "Palletised cargo", description: "Supplies, loaded on pallets." },
  { id: "container", label: "20 ft container", description: "One standard 20 ft container." },
];

export const Vessels = () => {
  const [mode, setMode] = useState("empty");
  const [status, setStatus] = useState("loading");
  const [paused, setPaused] = useState(false);
  const onStatus = useCallback((value) => setStatus(value), []);
  const ref = useRef(null);
  const visible = useInView(ref, { once: true, margin: "200px" });
  const selected = MODES.find((m) => m.id === mode);
  return (
    <section id="vessels" ref={ref} className="shell py-20 sm:py-28" data-testid="vessels-section" data-analytics-section="vessels">
      <SectionIntro label="Our vessels" title="Meet Pratham." text="An unmanned supply vessel. Built to carry cargo, with people in charge from shore." />
      <div className="mt-10 grid grid-cols-3 border-b border-white/15" aria-label="Vessels">
        <div className="border-b-2 border-signal pb-4" aria-current="true"><p className="text-xl text-fg sm:text-3xl">Pratham</p><p className="mt-1 text-xs text-signal sm:text-sm">In development</p></div>
        {["Dashak", "Shatak"].map((name) => <button key={name} type="button" disabled className="cursor-not-allowed pb-4 text-left text-fg-3" aria-label={`${name}, coming soon`}><span className="block text-xl sm:text-3xl">{name}</span><span className="mt-1 block text-xs sm:text-sm">Coming soon</span></button>)}
      </div>
      <div className="relative mt-2 h-[300px] sm:h-[440px] lg:h-[520px]" data-testid="pratham-viewer" data-status={status} role="group" aria-label={`x1025 Pratham, ${selected.label}`}>
        {visible && <ViewerBoundary onStatus={onStatus}><Suspense fallback={null}><PrathamScene mode={mode} paused={paused} onModeChange={setMode} onStatus={onStatus} /></Suspense></ViewerBoundary>}
        {status !== "ready" && <img src={`/models/pratham/${mode}.png?v=0.5-finishes2`} alt={`x1025 Pratham supply vessel with ${selected.label.toLowerCase()}`} className="pointer-events-none absolute inset-0 h-full w-full object-contain" />}
        <div className="absolute bottom-2 right-0 flex gap-1.5" aria-label="Model view">
          <button type="button" onClick={() => { track(paused ? "vessel_play" : "vessel_pause"); setPaused((p) => !p); }} disabled={status !== "ready"} aria-label={paused ? "Play vessel rotation" : "Pause vessel rotation"} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-ink/70 text-fg-2 hover:border-signal hover:text-signal disabled:opacity-30">
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="flex flex-col items-start justify-between gap-5 border-t border-white/10 pt-5 md:flex-row md:items-center">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Cargo configuration">
          {MODES.map((m) => <button key={m.id} type="button" onClick={() => { track(`cargo_${m.id}`); setMode(m.id); }} aria-pressed={mode === m.id} className={`min-h-11 rounded-full border px-4 text-sm transition-colors ${mode === m.id ? "border-signal bg-signal text-ink" : "border-white/20 text-fg-2 hover:border-signal hover:text-fg"}`}>{m.label}</button>)}
        </div>
        <p className="text-sm text-fg-2" aria-live="polite">{selected.description}</p>
      </div>
      <p className="mt-4 text-xs text-fg-3">Design preview · Actual vessel model · Colours shown for illustration</p>
      <div className="mt-9 flex flex-col items-start justify-between gap-5 border-t border-white/10 pt-7 sm:flex-row sm:items-center">
        <p className="max-w-md text-lg text-fg-2">Tell us what you need to carry, and where.</p>
        <PillLink to="/pratham/enquire" testId="pratham-enquire-cta" data-analytics-event="cta_pratham" className="shrink-0">Enquire about Pratham</PillLink>
      </div>
    </section>
  );
};
